/**
 * essentialsBuffer — §A (Essentials / Identity)'s explicit-save edit buffer.
 *
 * Pure state + coercion for `SectionEssentials`, extracted so the buffering
 * rules are testable without a DOM. Under the explicit-save contract
 * (ai-docs/architect-briefs/bb-phase2-explicit-save-brief-20260810.md) §A's
 * edits no longer reach the wire on blur/commit — they land here, and the
 * section's registered `save()` replays them through the SAME page callbacks
 * the panel already used. Verify / unverify / "Verify all" stay immediate and
 * never touch this buffer: they are operator acts, not edits.
 *
 * Five sub-maps, one per §A write path (see .scratch/bb-save-lane-prep/
 * section-A-write-paths.md):
 *
 *  - `draft`   — the flat/legacy-draft-backed scalars (business_name, website,
 *                phone, email, description, address). These are DUAL-WRITE:
 *                their flat legacy column AND a v2 home (bb_identity /
 *                bb_locations). save() commits them through the page's
 *                `commitDraftFields` seam, whose `pushDraftToV2` writes the v2
 *                half per-field and whose legacy PUT writes the flat half.
 *  - `serviceLocations` — the service-area array (draft-backed, diff-synced).
 *  - `identity` — bb_identity scalars with NO flat echo (dba, founded_year,
 *                team_size, booking_url); replayed as narrow saveIdentity.
 *  - `owner`   — bb_identity.owner merge-patch subkeys (name/title/on_camera).
 *  - `socials` — bb_identity.socials merge-patch delta (url, or null = delete).
 *  - `location` — bb_locations primary-row columns with no draft key
 *                (service_radius_miles, tracking_phone, gbp_url, hours).
 *
 * Two deliberate shapes, mirroring proofBuffer:
 *
 *  - **Raw, not typed.** Text/number fields buffer their as-typed string so an
 *    input can render a mid-typing state ("19", "") no typed payload could
 *    hold; coercion (founded_year/team_size → number, radius → int/null)
 *    happens once, at save time.
 *  - **Diffed against the store, never blind.** Every setter DROPS a field
 *    whose new value equals the current stored value, so typing an edit and
 *    typing it back leaves the section clean and the "n changes" count honest.
 *    This is the same discipline the §B prod incident asks for: buffered ops
 *    stay narrow and are re-diffed; nothing is synthesized from a list compare.
 */
import type { IdentityPayload, LocationPayload } from '@/features/brand-bible-wizard/types';

/** Flat/legacy-draft-backed scalar fields (dual-write via commitDraftFields). */
export type DraftScalarField =
  | 'business_name'
  | 'website'
  | 'phone'
  | 'email'
  | 'description'
  | 'address';

/** bb_identity scalars with no flat echo — replayed as narrow saveIdentity. */
export type IdentityScalarField = 'dba' | 'founded_year' | 'team_size' | 'booking_url';

/** bb_identity.owner merge-patch subkeys. */
export type OwnerField = 'name' | 'title' | 'on_camera_ok';

/** bb_locations primary-row columns with no draft key. */
export type LocationField = 'service_radius_miles' | 'tracking_phone' | 'gbp_url' | 'hours';

export interface EssentialsBuffer {
  draft: Partial<Record<DraftScalarField, string>>;
  /** undefined = not edited; an array (possibly empty) = the desired list. */
  serviceLocations?: string[];
  identity: Partial<Record<IdentityScalarField, string>>;
  owner: Partial<{ name: string; title: string; on_camera_ok: boolean }>;
  /** platform -> url (add/change) or null (remove a stored key). */
  socials: Record<string, string | null>;
  location: Partial<{
    service_radius_miles: number | null;
    tracking_phone: string;
    gbp_url: string;
    hours: Record<string, string>;
  }>;
}

export const EMPTY_ESSENTIALS_BUFFER: EssentialsBuffer = {
  draft: {},
  identity: {},
  owner: {},
  socials: {},
  location: {},
};

/* --------------------------------- reads ---------------------------------- */

const arraysEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const hoursEqual = (
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined,
): boolean => {
  const ak = Object.keys(a ?? {});
  const bk = Object.keys(b ?? {});
  if (ak.length !== bk.length) return false;
  return ak.every((k) => (a ?? {})[k] === (b ?? {})[k]);
};

/**
 * Number of buffered edits, feeding the footer "Save A · {n} changes" label.
 * Each buffered scalar counts once; the service-locations array, each owner
 * subkey, each social platform, and each location column (hours included)
 * count once. Deliberately field-level: three identity edits honestly read as
 * "3 changes".
 */
export function changeCount(buffer: EssentialsBuffer): number {
  return (
    Object.keys(buffer.draft).length +
    (buffer.serviceLocations !== undefined ? 1 : 0) +
    Object.keys(buffer.identity).length +
    Object.keys(buffer.owner).length +
    Object.keys(buffer.socials).length +
    Object.keys(buffer.location).length
  );
}

export const isDirty = (buffer: EssentialsBuffer): boolean => changeCount(buffer) > 0;

/** The value a controlled draft input should display: buffered edit if any,
 *  else the stored draft value. */
export function bufferedDraft(buffer: EssentialsBuffer, field: DraftScalarField, stored: string): string {
  return buffer.draft[field] ?? stored;
}

/** The service-area array to display: buffered if edited, else stored. */
export function bufferedServiceLocations(buffer: EssentialsBuffer, stored: string[]): string[] {
  return buffer.serviceLocations ?? stored;
}

/** The effective on_camera toggle state: buffered if edited, else stored. */
export function bufferedOnCamera(buffer: EssentialsBuffer, stored: boolean | undefined): boolean {
  return buffer.owner.on_camera_ok ?? Boolean(stored);
}

/**
 * The effective socials map to render: the stored map with buffered deltas
 * applied (a null delta removes the platform). Never a blind spread — the
 * buffer only ever holds the CHANGED platforms.
 */
export function bufferedSocials(
  buffer: EssentialsBuffer,
  stored: Record<string, string | null>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [platform, url] of Object.entries(stored)) {
    if (typeof url === 'string' && url.trim() !== '') out[platform] = url;
  }
  for (const [platform, url] of Object.entries(buffer.socials)) {
    if (url === null) delete out[platform];
    else out[platform] = url;
  }
  return out;
}

/* -------------------------------- mutations ------------------------------- */

/** Buffer one flat/draft scalar. `stored` is the row's current draft value —
 *  an edit back to it removes the field rather than recording a no-op. */
export function setDraftField(
  buffer: EssentialsBuffer,
  field: DraftScalarField,
  next: string,
  stored: string,
): EssentialsBuffer {
  const draft = { ...buffer.draft };
  if (next === stored) delete draft[field];
  else draft[field] = next;
  return { ...buffer, draft };
}

export function setServiceLocations(
  buffer: EssentialsBuffer,
  next: string[],
  stored: string[],
): EssentialsBuffer {
  if (arraysEqual(next, stored)) {
    const { serviceLocations: _drop, ...rest } = buffer;
    void _drop;
    return { ...rest, serviceLocations: undefined };
  }
  return { ...buffer, serviceLocations: next };
}

export function setIdentityField(
  buffer: EssentialsBuffer,
  field: IdentityScalarField,
  next: string,
  stored: string,
): EssentialsBuffer {
  const identity = { ...buffer.identity };
  if (next === stored) delete identity[field];
  else identity[field] = next;
  return { ...buffer, identity };
}

export function setOwnerField(
  buffer: EssentialsBuffer,
  field: 'name' | 'title',
  next: string,
  stored: string,
): EssentialsBuffer {
  const owner = { ...buffer.owner };
  if (next === stored) delete owner[field];
  else owner[field] = next;
  return { ...buffer, owner };
}

export function setOwnerOnCamera(
  buffer: EssentialsBuffer,
  next: boolean,
  stored: boolean,
): EssentialsBuffer {
  const owner = { ...buffer.owner };
  if (next === stored) delete owner.on_camera_ok;
  else owner.on_camera_ok = next;
  return { ...buffer, owner };
}

/**
 * Stage a social add/change (`url`) or removal (`null`). `stored` is the
 * current persisted map. Adding a platform back to its stored url, or removing
 * a platform that was only a buffered add, cancels out — the buffer stays as
 * small as the real edit set.
 */
export function setSocial(
  buffer: EssentialsBuffer,
  platform: string,
  url: string | null,
  stored: Record<string, string | null>,
): EssentialsBuffer {
  const socials = { ...buffer.socials };
  const storedUrl = stored[platform];
  const storedHas = typeof storedUrl === 'string' && storedUrl.trim() !== '';

  if (url === null) {
    // Removing a platform that only exists as a buffered add: just drop the add.
    if (!storedHas) delete socials[platform];
    else socials[platform] = null;
  } else if (storedHas && storedUrl === url) {
    // Re-adding the exact stored value: no change.
    delete socials[platform];
  } else {
    socials[platform] = url;
  }
  return { ...buffer, socials };
}

export function setLocationScalar(
  buffer: EssentialsBuffer,
  field: 'tracking_phone' | 'gbp_url',
  next: string,
  stored: string,
): EssentialsBuffer {
  const location = { ...buffer.location };
  if (next === stored) delete location[field];
  else location[field] = next;
  return { ...buffer, location };
}

/** Radius: integer-or-null. `next === null` clears the column; a non-integer
 *  is rejected by the caller before it reaches here. */
export function setLocationRadius(
  buffer: EssentialsBuffer,
  next: number | null,
  stored: number | null,
): EssentialsBuffer {
  const location = { ...buffer.location };
  if (next === stored) delete location.service_radius_miles;
  else location.service_radius_miles = next;
  return { ...buffer, location };
}

/** Hours: a COMPLETE day→hours map (the backend replaces the whole JSONB
 *  column). An edit back to the stored map drops the buffer entry. */
export function setLocationHours(
  buffer: EssentialsBuffer,
  next: Record<string, string>,
  stored: Record<string, string> | undefined,
): EssentialsBuffer {
  const location = { ...buffer.location };
  if (hoursEqual(next, stored)) delete location.hours;
  else location.hours = next;
  return { ...buffer, location };
}

/* -------------------------------- coercion -------------------------------- */

/** Build the narrow saveIdentity patch for one buffered identity scalar,
 *  matching the pre-buffer onCommit coercion exactly (blank number → undefined,
 *  non-numeric → undefined). */
export function coerceIdentityField(
  field: IdentityScalarField,
  raw: string,
): Partial<IdentityPayload> {
  switch (field) {
    case 'founded_year':
      return { founded_year: raw.trim() ? Number(raw) || undefined : undefined };
    case 'team_size':
      return { team_size: raw.trim() ? Number(raw) || undefined : undefined };
    case 'dba':
      return { dba: raw };
    case 'booking_url':
      return { booking_url: raw };
  }
}

/** Build the narrow savePrimaryLocation patch from the buffered location
 *  sub-map. `hours` is already a complete map, so a single write is correct. */
export function coerceLocationPatch(buffer: EssentialsBuffer): Partial<LocationPayload> {
  const patch: Partial<LocationPayload> = {};
  if ('service_radius_miles' in buffer.location) {
    // null clears the column; LocationPayload types this number|undefined, so
    // the null clear casts through exactly as the pre-buffer onCommit did.
    patch.service_radius_miles = buffer.location.service_radius_miles as unknown as number | undefined;
  }
  if ('tracking_phone' in buffer.location) patch.tracking_phone = buffer.location.tracking_phone;
  if ('gbp_url' in buffer.location) patch.gbp_url = buffer.location.gbp_url;
  if ('hours' in buffer.location) patch.hours = buffer.location.hours;
  return patch;
}
